"use client"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import type { PublicPage } from "../content/content-types.ts"
import { AuthenticationRequiredError, loadUserView, UserViewRequestError } from "../lib/api-client.ts"
import { loadPublicLegalDocument } from "../lib/public-content-client.ts"
import { routeAction } from "../lib/route-actions.ts"
import type { UserRoute } from "../lib/routes.ts"
import type { UserView } from "../lib/user-view.ts"
import { type AcknowledgementKind, ActionForm } from "./action-form.tsx"
import { homeHeaderBalanceFromView, useHomeHeaderBalancePublisher } from "./app-shell.tsx"
import { useConsumerSessionMemory } from "./consumer-session.tsx"
import { FailurePanel } from "./failure-panel.tsx"
import { JourneyLoading } from "./journey-loading.tsx"
import { LogoutControl } from "./logout-control.tsx"
import { isMainTab, MainTabHeading } from "./main-tab-heading.tsx"
import type { MyLegalDocuments } from "./my-legal-dialogs.tsx"
import { MyLinkGroups } from "./my-link-groups.tsx"
import { NotificationsView } from "./notifications-view.tsx"
import { PreferencesView } from "./preferences-view.tsx"
import { StatePanel } from "./state-panel.tsx"
import { SupportView } from "./support-view.tsx"
import { type SystemStateKind, SystemStatePanel } from "./system-state-panel.tsx"
import { ViewBody } from "./view-body.tsx"

type LoadState =
  | {
      readonly kind: "loading"
    }
  | {
      readonly kind: "loaded"
      readonly view: UserView
    }
  | {
      readonly kind: "failed"
      readonly source: "api" | "network"
    }
export const JourneyClient = ({ guidePage, legalDocuments, pathname, route, search }: { readonly guidePage?: PublicPage; readonly legalDocuments?: MyLegalDocuments; readonly pathname: string; readonly route: UserRoute; readonly search: string }) => {
  const router = useRouter()
  const memory = useConsumerSessionMemory()
  const viewKey = `${pathname}${search.length === 0 ? "" : `?${search}`}`
  const publishHomeHeaderBalance = useHomeHeaderBalancePublisher()
  const [snapshot, setSnapshot] = useState<{
    readonly key: string
    readonly state: LoadState
  }>(() => {
    const view = memory?.readView(viewKey)
    return { key: viewKey, state: view === undefined ? { kind: "loading" } : { kind: "loaded", view } }
  })
  const cached = memory?.readView(viewKey)
  const loaded: LoadState = snapshot.key === viewKey ? snapshot.state : cached === undefined ? { kind: "loading" } : { kind: "loaded", view: cached }
  const [myLegalDocuments, setMyLegalDocuments] = useState(legalDocuments ?? memory?.legalDocuments)
  const resolvedLegalDocuments = legalDocuments ?? myLegalDocuments ?? memory?.legalDocuments
  const searchParams = new URLSearchParams(search)
  const statusSurface = route.stateProfile === "TRANSACTION" ? "financial" : "view"
  const publishesHomeHeaderBalance = route.id === "APP-HOME-001"
  const systemState: SystemStateKind | null = route.id === "APP-SYS-001" ? "maintenance" : route.id === "APP-SYS-002" ? "update" : null
  useEffect(() => {
    if (systemState !== null) return
    const controller = new AbortController()
    const epoch = memory?.epoch ?? 0
    const previous = memory?.readView(viewKey)
    setSnapshot({ key: viewKey, state: previous === undefined ? { kind: "loading" } : { kind: "loaded", view: previous } })
    void loadUserView(route, pathname, new URLSearchParams(search), controller.signal)
      .then((view) => {
        if (controller.signal.aborted || (memory !== null && memory.epoch !== epoch)) return
        memory?.writeView(viewKey, view, epoch)
        setSnapshot({ key: viewKey, state: { kind: "loaded", view } })
        if (!publishesHomeHeaderBalance) return
        const balance = homeHeaderBalanceFromView(view)
        if (balance !== null) publishHomeHeaderBalance(balance)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || (memory !== null && memory.epoch !== epoch) || (error instanceof Error && error.name === "AbortError")) return
        if (error instanceof AuthenticationRequiredError) {
          memory?.clear()
          router.replace(`/login?returnTo=${encodeURIComponent(pathname)}`)
          return
        }
        if (previous !== undefined && !(error instanceof UserViewRequestError)) return
        setSnapshot({ key: viewKey, state: { kind: "failed", source: error instanceof UserViewRequestError ? "api" : "network" } })
      })
    return () => controller.abort()
  }, [memory, pathname, publishHomeHeaderBalance, publishesHomeHeaderBalance, route, router, search, systemState, viewKey])
  useEffect(() => {
    if (route.id !== "APP-MY-001" || legalDocuments !== undefined || memory?.legalDocuments !== undefined) return
    let active = true
    const epoch = memory?.epoch
    void Promise.all([loadPublicLegalDocument("terms-service"), loadPublicLegalDocument("terms-privacy")]).then(([service, privacy]) => {
      if (!active || memory?.epoch !== epoch || !service.document.publishable || !privacy.document.publishable) return
      const documents = { service: service.document, privacy: privacy.document }
      if (memory !== null) memory.legalDocuments = documents
      setMyLegalDocuments(documents)
    })
    return () => {
      active = false
    }
  }, [legalDocuments, memory, route.id])
  if (systemState !== null) return <SystemStatePanel kind={systemState} />
  if (loaded.kind === "loading") return memory === null ? <StatePanel description="최신 정보를 불러오고 있어요." pageHeading={route.title} state="LOADING" surface={statusSurface} /> : <JourneyLoading route={route} />
  if (loaded.kind === "failed") return <StatePanel description={loaded.source === "api" ? "잠시 후 다시 시도해 주세요." : "인터넷 연결을 확인한 뒤 다시 시도해 주세요."} pageHeading={route.title} state="ERROR" surface={statusSurface} />
  const view = loaded.view
  const action = routeAction(route, pathname, searchParams)
  if (view.failureCause !== undefined) return <FailurePanel action={action} cause={view.failureCause} reference={view.kind === "transaction" ? view.reference : undefined} />
  if (view.state !== "DEFAULT")
    return view.kind === "transaction" ? <StatePanel description={view.description} pageHeading={route.title} reference={view.reference} state={view.state} surface={statusSurface} /> : <StatePanel description={view.description} pageHeading={route.title} state={view.state} surface={statusSurface} />
  const acknowledgementKind: AcknowledgementKind = route.id === "APP-DEL-001" ? "deletion" : route.id === "APP-AUTH-002" ? "consent" : route.id === "APP-TRF-001" || route.id === "APP-RDM-001" ? "financial" : null
  const destinationLinks = route.id === "APP-ORD-002" ? [{ href: "/support/inquiries", label: "문의하기" }] : []
  return (
    <article className="journey-card" data-app-content-path={pathname} data-route-id={route.id} data-route-kind={route.kind} data-view-kind={view.kind}>
      {isMainTab(route.id) ? (
        <MainTabHeading routeId={route.id} />
      ) : (
        <header className="journey-heading">
          <h1>{view.heading}</h1>
          <p className="phrase-safe-block">{view.description}</p>
        </header>
      )}
      {view.kind === "notifications" ? <NotificationsView view={view} /> : view.kind === "preferences" ? <PreferencesView view={view} /> : view.kind === "support" ? <SupportView view={view} /> : route.id === "APP-MY-001" ? null : <ViewBody {...(guidePage === undefined ? {} : { guidePage })} view={view} />}
      {route.id === "APP-MY-001" ? <MyLinkGroups details={view.kind === "page" ? view.details : []} {...(resolvedLegalDocuments === undefined ? {} : { legalDocuments: resolvedLegalDocuments })} /> : null}
      {destinationLinks.length === 0 ? null : (
        <nav aria-label={`${route.title} 바로가기`} className="destination-links">
          {destinationLinks.map((link) => (
            <Link className="button secondary" href={link.href} key={link.href}>
              {link.label}
            </Link>
          ))}
        </nav>
      )}
      {route.id === "APP-MY-001" ? <LogoutControl /> : null}
      {view.kind === "notifications" || view.kind === "preferences" || view.kind === "support" || action === null ? null : <ActionForm acknowledgementKind={acknowledgementKind} action={action} />}
    </article>
  )
}
